import {
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
  useState,
} from "react";
import { StandaloneFacetedFilter } from "../StandaloneFacetedFilter";
import { LeveledFiltersConfig } from "@/components/data-table/data-table";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Eye, EyeOff, X, Plus, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDocumentMetadata } from "@/hooks/useWidgetData";
import {
  WORKSPACE_DOCUMENT_FILTER_PREFIX,
  WORKSPACE_DOCUMENT_TITLE_FILTER_KEY,
} from "@/pages/global-search/constants/searchFilters";
import {
  buildFilterOptions,
} from "@/pages/legislation/utils/ministryFilterOptions";

/** Standard (reserved) workspace document filter – only document title is supported by the backend for now. */
const STANDARD_WORKSPACE_DOCUMENT_FILTERS: {
  key: string;
  label: string;
  placeholder: string;
}[] = [
  {
    key: WORKSPACE_DOCUMENT_TITLE_FILTER_KEY,
    label: "Dokumenttitel",
    placeholder: "Tilføj søgetekst...",
  },
];

interface GlobalSearchFiltersProps {
  leveledFilters?: LeveledFiltersConfig;
  filters: Record<string, string[]>;
  workspaceDocumentFilters?: Record<string, string[]>;
  defaultsPlan: {
    sourceKey: string;
    childFilters: { id: string; value: unknown }[];
  } | null;
  onFilterChange: (filterKey: string, values: string[]) => void;
  sources: string[];
  setSources: (sources: string[]) => void;
  /** Per-source result counts (e.g., {"retsinformation": 10, "arvid": 5}) */
  sourceCounts?: Record<string, number>;
  focusedSource: string | null;
  setFocusedSource: Dispatch<SetStateAction<string | null>>;
  className?: string;
}

const GlobalSearchFilters = ({
  leveledFilters,
  filters,
  workspaceDocumentFilters = {},
  defaultsPlan,
  onFilterChange,
  sources,
  setSources,
  sourceCounts,
  focusedSource,
  setFocusedSource,
  className,
}: GlobalSearchFiltersProps) => {
  const [standardFilterInputs, setStandardFilterInputs] = useState<
    Record<string, string>
  >({});
  const [addedMetadataFilterKeys, setAddedMetadataFilterKeys] = useState<
    string[]
  >([]);
  const [addFilterPopoverOpen, setAddFilterPopoverOpen] = useState(false);
  const { data: metadataFields = [] } = useDocumentMetadata(undefined);

  // Build root (level 1) options from config sources
  const rootOptions = useMemo(() => {
    if (!leveledFilters) return [];
    return Object.entries(leveledFilters.levels).map(([sourceKey, cfg]) => ({
      label: cfg.displayName ?? sourceKey,
      value: sourceKey,
    }));
  }, [leveledFilters]);

  // Helper: clear level-2 filters for specific sources (using namespaced keys)
  const clearFiltersForSources = useCallback(
    (sourcesToClear: string[]) => {
      if (!leveledFilters) return;
      sourcesToClear.forEach((sourceKey) => {
        const sourceCfg = leveledFilters.levels[sourceKey];
        if (sourceCfg) {
          Object.keys(sourceCfg.filters).forEach((k) =>
            onFilterChange(`${sourceKey}::${k}`, [])
          );
        }
      });
    },
    [leveledFilters, onFilterChange]
  );

  // Helper: clear all level-2 filters for ALL sources (using namespaced keys)
  const clearAllLevel2 = useCallback(() => {
    if (!leveledFilters) return;
    const allChildKeys = new Set<string>();
    Object.entries(leveledFilters.levels).forEach(([sourceKey, s]) => {
      Object.keys(s.filters).forEach((k) =>
        allChildKeys.add(`${sourceKey}::${k}`)
      );
    });
    allChildKeys.forEach((k) => onFilterChange(k, []));
  }, [leveledFilters, onFilterChange]);

  // Clear workspace document filters (wd:: prefix) and reset added metadata filter list
  const clearWorkspaceDocumentFilters = useCallback(() => {
    Object.keys(workspaceDocumentFilters).forEach((k) =>
      onFilterChange(`${WORKSPACE_DOCUMENT_FILTER_PREFIX}${k}`, [])
    );
    setAddedMetadataFilterKeys([]);
  }, [workspaceDocumentFilters, onFilterChange]);

  // Clear all filters
  const clearAllFilters = () => {
    clearAllLevel2();
    clearWorkspaceDocumentFilters();
    setSources([]);
  };

  // Check if any filters are applied
  const hasActiveFilters =
    sources.length > 0 ||
    Object.keys(filters).length > 0 ||
    Object.keys(workspaceDocumentFilters).length > 0;

  // Handle source selection changes (multi-select)
  const handleSourceChange = useCallback(
    (newSources: string[]) => {
      // Find removed sources and clear their child filters
      const removedSources = sources.filter((s) => !newSources.includes(s));
      clearFiltersForSources(removedSources);

      // Find newly added sources and apply defaults if applicable
      const addedSources = newSources.filter((s) => !sources.includes(s));
      addedSources.forEach((addedSource) => {
        if (defaultsPlan && defaultsPlan.sourceKey === addedSource) {
          for (const cf of defaultsPlan.childFilters || []) {
            const val = cf.value;
            const arr = Array.isArray(val)
              ? val.map(String)
              : val != null
                ? [String(val)]
                : [];
            onFilterChange(`${addedSource}::${cf.id}`, arr);
          }
        }
      });

      setSources(newSources);
    },
    [sources, clearFiltersForSources, defaultsPlan, onFilterChange, setSources]
  );

  const handleFocusToggle = useCallback(
    (sourceKey: string) => {
      setFocusedSource((prev: string | null) =>
        prev === sourceKey ? null : sourceKey
      );
    },
    [setFocusedSource]
  );

  const handleRemoveSource = useCallback(
    (sourceKey: string) => {
      const newSources = sources.filter((s) => s !== sourceKey);
      handleSourceChange(newSources);
      // Also clear the focused state if this source was focused
      if (focusedSource === sourceKey) {
        setFocusedSource(null);
      }
    },
    [sources, handleSourceChange, focusedSource, setFocusedSource]
  );

  const handleAddStandardFilterValue = useCallback(
    (filterKey: string) => {
      const input = standardFilterInputs[filterKey]?.trim() ?? "";
      if (!input) return;
      const current =
        workspaceDocumentFilters[filterKey] || [];
      onFilterChange(WORKSPACE_DOCUMENT_FILTER_PREFIX + filterKey, [...current, input]);
      setStandardFilterInputs((prev) => ({ ...prev, [filterKey]: "" }));
    },
    [standardFilterInputs, workspaceDocumentFilters, onFilterChange]
  );

  const handleRemoveStandardFilterValue = useCallback(
    (filterKey: string, value: string) => {
      const current = workspaceDocumentFilters[filterKey] || [];
      onFilterChange(
                    WORKSPACE_DOCUMENT_FILTER_PREFIX + filterKey,
                    current.filter((v) => v !== value)
                  );
    },
    [workspaceDocumentFilters, onFilterChange]
  );

  const isMetadataFilterShown = useCallback(
    (fieldName: string) =>
      addedMetadataFilterKeys.includes(fieldName) ||
      (workspaceDocumentFilters[fieldName]?.length ?? 0) > 0,
    [addedMetadataFilterKeys, workspaceDocumentFilters]
  );

  const availableMetadataFieldsToAdd = useMemo(
    () =>
      metadataFields.filter(
        (field) =>
          (field.distinctValues?.length ?? 0) > 0 &&
          !isMetadataFilterShown(field.fieldName)
      ),
    [metadataFields, isMetadataFilterShown]
  );

  const shownMetadataFields = useMemo(
    () =>
      metadataFields.filter(
        (field) =>
          (field.distinctValues?.length ?? 0) > 0 &&
          isMetadataFilterShown(field.fieldName)
      ),
    [metadataFields, isMetadataFilterShown]
  );

  const handleAddMetadataFilter = useCallback((fieldName: string) => {
    setAddedMetadataFilterKeys((prev) =>
      prev.includes(fieldName) ? prev : [...prev, fieldName]
    );
    setAddFilterPopoverOpen(false);
  }, []);

  const handleRemoveMetadataFilter = useCallback(
    (fieldName: string) => {
      setAddedMetadataFilterKeys((prev) => prev.filter((k) => k !== fieldName));
      onFilterChange(WORKSPACE_DOCUMENT_FILTER_PREFIX + fieldName, []);
    },
    [onFilterChange]
  );

  const hasLegislationFilters = !!leveledFilters;

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-border">
        <h3 className="text-sm font-semibold">Filtre</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
          >
            <X className="w-3 h-3 mr-1" />
            Ryd alle
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 pt-4 overflow-y-auto flex-1">
        {/* Legislation Filters Section */}
        {hasLegislationFilters && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Lovgivning
          </h4>

          {/* Root (level 1) - Source filter - MULTI SELECT */}
          <div className="space-y-2">
            <StandaloneFacetedFilter
              title="Kilde"
              options={rootOptions}
              selectedValues={sources}
              selectionMode="multi"
              onValueChange={handleSourceChange}
              hideSelectedLabels
            />
          </div>

          {/* Level 2 filters - grouped by each selected source */}
          {sources.map((sourceKey) => {
            const sourceCfg = leveledFilters.levels[sourceKey];
            if (!sourceCfg) return null;

            const childEntries = Object.entries(sourceCfg.filters);
            if (childEntries.length === 0) return null;

            // Get count for this source (lowercase key match)
            const sourceCount = sourceCounts?.[sourceKey.toLowerCase()];
            // focusedSource now acts as "hidden source" - clicking eye hides that source
            const isHidden = focusedSource === sourceKey;

            return (
              <div
                key={sourceKey}
                className={cn(
                  "flex flex-col gap-2 p-3 rounded-md border border-border bg-muted/30 transition-opacity",
                  isHidden && "opacity-40"
                )}
              >
                <div className="flex items-center justify-between pointer-events-auto">
                  <span className="text-xs font-semibold text-foreground">
                    {sourceCfg.displayName ?? sourceKey}
                  </span>
                  <div className="flex items-center gap-1 ml-auto">
                    {sourceCount !== undefined && (
                      <span className="text-xs text-muted-foreground">
                        ({sourceCount})
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-6 w-6 text-muted-foreground hover:text-foreground",
                        isHidden && "text-foreground"
                      )}
                      onClick={() => handleFocusToggle(sourceKey)}
                      aria-label={
                        isHidden
                          ? `Vis ${sourceCfg.displayName ?? sourceKey} igen`
                          : `Skjul ${sourceCfg.displayName ?? sourceKey}`
                      }
                    >
                      {isHidden ? (
                        <EyeOff className="h-3.5 w-3.5" />
                      ) : (
                        <Eye className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveSource(sourceKey)}
                      aria-label={`Fjern ${sourceCfg.displayName ?? sourceKey}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className={cn(isHidden && "pointer-events-none")}>
                  {childEntries.map(([key, cfg]) => {
                    const namespacedKey = `${sourceKey}::${key}`;
                    const selectionMode =
                      cfg.type === "Single" ? "single" : "multi";
                    const stringOptions = (cfg.options || []).map((o) =>
                      typeof o === "string" ? o : o.value
                    );
                    const opts = buildFilterOptions(
                      key,
                      stringOptions,
                      cfg.optionAliases
                    );
                    return (
                      <StandaloneFacetedFilter
                        key={`${sourceKey}-${key}`}
                        title={cfg.displayName || key}
                        options={opts}
                        filterKey={key}
                        selectedValues={filters[namespacedKey] || []}
                        selectionMode={selectionMode}
                        onValueChange={(values) =>
                          onFilterChange(namespacedKey, values)
                        }
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* Workspace Document Filters Section */}
        <div className="space-y-3">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Workspace dokumenter
          </h4>

          {/* Document title (reserved filter); metadata filters added via dropdown below */}
          {STANDARD_WORKSPACE_DOCUMENT_FILTERS.map((filter) => {
            const filterKey = filter.key;
            const values = workspaceDocumentFilters[filterKey] || [];
            const inputValue = standardFilterInputs[filterKey] ?? "";
            return (
              <div key={filterKey} className="space-y-2">
                <span className="text-xs font-medium text-foreground">
                  {filter.label}
                </span>
                <div className="flex gap-1">
                  <Input
                    placeholder={filter.placeholder}
                    value={inputValue}
                    onChange={(e) =>
                      setStandardFilterInputs((prev) => ({
                        ...prev,
                        [filterKey]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddStandardFilterValue(filterKey);
                      }
                    }}
                    className="h-8 text-xs flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 shrink-0"
                    onClick={() => handleAddStandardFilterValue(filterKey)}
                    aria-label={`Tilføj ${filter.label.toLowerCase()}-filter`}
                  >
                    <Plus className="size-3.5" />
                  </Button>
                </div>
                {values.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {values.map((v) => (
                      <span
                        key={v}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted text-xs"
                      >
                        {v}
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveStandardFilterValue(filterKey, v)
                          }
                          className="hover:text-destructive rounded p-0.5"
                          aria-label={`Fjern ${v}`}
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add metadata filter dropdown */}
          {availableMetadataFieldsToAdd.length > 0 && (
            <div className="space-y-2">
              <Popover
                open={addFilterPopoverOpen}
                onOpenChange={setAddFilterPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-full border-dashed text-xs justify-start gap-1"
                  >
                    <Plus className="size-3.5 shrink-0" />
                    Tilføj metadatafilter
                    <ChevronDown className="size-3.5 ml-auto opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-1 z-130 min-w-[180px] max-h-[280px] overflow-y-auto"
                  align="start"
                  onClick={(e) => e.stopPropagation()}
                >
                  {availableMetadataFieldsToAdd.map((field) => (
                    <button
                      key={field.fieldName}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-xs rounded-sm hover:bg-muted focus:bg-muted focus:outline-none"
                      onClick={() => handleAddMetadataFilter(field.fieldName)}
                    >
                      {field.fieldName}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Metadata field filters (only when added by user or have values) */}
          {shownMetadataFields.map((field) => {
            const options = (field.distinctValues || []).map((val) => ({
              label: val,
              value: val,
            }));
            const selected =
              workspaceDocumentFilters[field.fieldName] ||
              workspaceDocumentFilters[field.externalPropertyName || ""] ||
              [];
            return (
              <div
                key={field.fieldName}
                className="flex items-start gap-1 group"
              >
                <div className="flex-1 min-w-0">
                  <StandaloneFacetedFilter
                    title={field.fieldName}
                    options={options}
                    selectedValues={selected}
                    selectionMode="multi"
                    onValueChange={(values) =>
                      onFilterChange(WORKSPACE_DOCUMENT_FILTER_PREFIX + field.fieldName, values)
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemoveMetadataFilter(field.fieldName)}
                  aria-label={`Fjern ${field.fieldName}-filter`}
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchFilters;
